import { Box, Button, CircularProgress, Paper, Typography, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { invoiceServices } from "../../services/invoiceServices";
import { useSnackbar } from "../../utils/useSnackbar";
import PageHeader from "../ui/PageHeader";
import ActionProgressDialog from '../common/ActionProgressDialog';
import "../../css/Invoice.css";

const InvoicePreview = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showSnackbar, SnackbarComponent } = useSnackbar();
    const { id, clientId, clientName, contactPersonName, clientAddress, startDate, endDate, invoiceType, invoiceDetails, country } = location.state || {};
    const [previewInvoice, setPreviewInvoice] = useState("");
    const [loading, setLoading] = useState(true);
    const [generatingType, setGeneratingType] = useState(null); // 'pdf' | 'excel' | null

    const [scale, setScale] = useState(1);
    const containerRef = useRef(null);

    useEffect(() => {
        const updateScale = () => {
            if (containerRef.current) {
                const width = containerRef.current.clientWidth;
                if (width > 0 && width < 820) {
                    setScale(width / 820);
                } else {
                    setScale(1);
                }
            }
        };

        let observer;
        if (containerRef.current && window.ResizeObserver) {
            observer = new ResizeObserver(updateScale);
            observer.observe(containerRef.current);
        } else {
            window.addEventListener('resize', updateScale);
        }

        updateScale();
        const timeoutId = setTimeout(updateScale, 100);

        return () => {
            if (observer) observer.disconnect();
            window.removeEventListener('resize', updateScale);
            clearTimeout(timeoutId);
        };
    }, [previewInvoice, loading]);

    useEffect(() => {
        const loadHtml = async () => {
            if (!location.state) return;
            try {
                const html = await invoiceServices.getPreviewInvoice(location.state);
                setPreviewInvoice(html);
            } catch (err) {
                showSnackbar("Failed to load invoice preview", "error");
            } finally {
                setLoading(false);
            }
        };
        loadHtml();
    }, [location.state, showSnackbar]);

    const handleBack = () => {
        navigate("/home/invoices", { state: location.state });
    };

    const handleErrorMessage = async (error, defaultMsg) => {
        if (error.response) {
            const responseData = error.response.data;

            if (responseData instanceof Blob) {
                try {
                    const text = await responseData.text();
                    const errorJson = JSON.parse(text);
                    showSnackbar(errorJson.message || defaultMsg, "error");
                } catch (parseError) {
                    try {
                        const text = await responseData.text();
                        showSnackbar(text || defaultMsg, "error");
                    } catch {
                        showSnackbar(defaultMsg, "error");
                    }
                }
            } else if (responseData && responseData.message) {
                showSnackbar(responseData.message, "error");
            } else if (typeof responseData === "string") {
                showSnackbar(responseData, "error");
            } else {
                showSnackbar(defaultMsg, "error");
            }
        } else {
            showSnackbar(defaultMsg, "error");
        }
    };

    const handleGenerateInvoicePdf = async () => {
        setGeneratingType('pdf');
        const payload = {
            id,
            clientId,
            clientName: clientName,
            contactPersonName: contactPersonName,
            clientAddress: clientAddress,
            startDate,
            endDate,
            invoiceType,
            invoiceDetails,
            country
        };
        try {
            await invoiceServices.generateInvoice(payload);
            showSnackbar("PDF Invoice Generated Successfully!", "success");
            navigate("/home/invoices");
        } catch (error) {
            await handleErrorMessage(error, "Failed to generate PDF invoice. Please try again.");
        } finally {
            setGeneratingType(null);
        }
    };

    const handleGenerateInvoiceExcel = async () => {
        setGeneratingType('excel');
        const payload = {
            id,
            clientId,
            clientName: clientName,
            contactPersonName: contactPersonName,
            clientAddress: clientAddress,
            startDate,
            endDate,
            invoiceType,
            invoiceDetails,
            country
        };
        try {
            await invoiceServices.generateInvoiceExcel(payload);
            showSnackbar("Excel Invoice Generated Successfully!", "success");
            navigate("/home/invoices");
        } catch (error) {
            await handleErrorMessage(error, "Failed to generate Excel invoice. Please try again.");
        } finally {
            setGeneratingType(null);
        }
    };

    if (!invoiceDetails && !loading) {
        return <Typography>No data found. Please go back and select jobs.</Typography>;
    }
    return (
        <Box className="invoice-page-container">
            <PageHeader
                title="Invoice Preview"
                left={
                    <IconButton onClick={() => navigate(-1)} size="medium">
                        <ArrowBackIcon />
                    </IconButton>
                }
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', width: { xs: "100%", sm: "auto" }, gap: 2, bgcolor: 'white', p: 1, borderRadius: 3, boxShadow: '0 2px 0.625rem rgba(0,0,0,0.05)', flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={handleBack}
                        disabled={generatingType !== null}
                        sx={{ borderRadius: 2, px: { xs: 2, sm: 3 }, flex: { xs: 1, sm: "none" } }}
                    >
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={generatingType === 'pdf' ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdfIcon />}
                        onClick={handleGenerateInvoicePdf}
                        disabled={generatingType !== null}
                        sx={{ borderRadius: 2, px: { xs: 2, sm: 3 }, flex: { xs: 1, sm: "none" } }}
                    >
                        {generatingType === 'pdf' ? "Generating PDF..." : "Generate PDF"}
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={generatingType === 'excel' ? <CircularProgress size={20} color="inherit" /> : <TableChartIcon />}
                        onClick={handleGenerateInvoiceExcel}
                        disabled={generatingType !== null}
                        sx={{ borderRadius: 2, px: { xs: 2, sm: 3 }, flex: { xs: 1, sm: "none" } }}
                    >
                        {generatingType === 'excel' ? "Generating Excel..." : "Generate Excel"}
                    </Button>
                </Box>
            </Box>

            <Paper className="invoice-table-card" elevation={3} sx={{ p: 0, overflow: 'hidden', height: { xs: "80vh", sm: "auto" }, display: "flex", flexDirection: "column" }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, height: { xs: 'auto', sm: 400 } }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box ref={containerRef} sx={{ flexGrow: 1, overflow: "hidden", position: "relative", width: "100%", height: { xs: "100%", sm: "50rem" }, bgcolor: '#f8f9fa' }}>
                        <Box
                            component="iframe"
                            title="invoice-preview"
                            srcDoc={previewInvoice}
                            sx={{
                                position: scale < 1 ? "absolute" : "static",
                                top: 0,
                                left: 0,
                                width: scale < 1 ? "820px" : "100%",
                                height: scale < 1 ? `${100 / scale}%` : "100%",
                                border: "none",
                                transform: scale < 1 ? `scale(${scale})` : "none",
                                transformOrigin: "top left",
                                backgroundColor: "white",
                            }}
                        />
                    </Box>
                )}
            </Paper>
            {SnackbarComponent}
            <ActionProgressDialog
                open={generatingType !== null}
                message={generatingType === 'excel' ? "Generating Excel Invoice..." : "Generating PDF Invoice..."}
            />
        </Box>
    );
};

export default InvoicePreview;